// Site notes (2026-10-25): a handling status and free text on a deficiency site, kept on the server
// (utils/site_notes.py) so everyone with access to the road sees and edits them. A note belongs to a
// site by where it is — corridor, analysis, direction and starting milepost — and is matched to the
// nearest site of the same analysis and direction within MATCH_MI (a re-run can move a start a little).
import { authHeaders } from '../../utils/authHeader';

export const NOTE_STATUSES = [
  ['open', 'Open', '#2563eb'], ['review', 'Under review', '#b45309'], ['planned', 'Planned', '#7c3aed'],
  ['done', 'Done', '#2f9e44'], ['none', 'No action needed', '#64748b'],
];
export const STATUS_LABEL = Object.fromEntries(NOTE_STATUSES.map(([k, l]) => [k, l]));
export const STATUS_COLOR = Object.fromEntries(NOTE_STATUSES.map(([k, , c]) => [k, c]));
export const MATCH_MI = 0.01;
export const MAX_NOTE = 2000;

async function call(method, path, params, body) {
  const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString() : '';
  const res = await fetch(`/api/safety-overview${path}${qs ? `?${qs}` : ''}`, {
    method, headers: { ...(await authHeaders()), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const scope = (seg) => ({ state: seg.state, route: seg.route, path_id: seg.pathId, project_id: seg.projectId || null });

/** The current notes of one segment's sites. */
export const fetchSiteNotes = (seg) => call('GET', '/site-notes', scope(seg)).then((d) => d.notes || []);
/** Every version of one note, newest first. */
export const fetchNoteHistory = (seg, note) => call('GET', '/site-notes/history',
  { ...scope(seg), analysis: note.analysis, direction: note.direction, mp: note.mpStart }).then((d) => d.history || []);
/** Save a new version of a site's note (under the matched note's own milepost, so its history stays one chain). */
export const saveSiteNote = (seg, site, current, status, note) => call('PUT', '/site-notes', null, {
  state: seg.state, route: seg.route, pathId: seg.pathId, projectId: seg.projectId || null, road: site.road,
  analysis: site.analysis, direction: site.direction, mpStart: current ? current.mpStart : site.mpStart,
  mpEnd: site.mpEnd ?? site.mpStart, status, note,
}).then((d) => d.note);

/** {siteId: note} — each site's note: same segment, analysis and direction, the nearest start within
 *  MATCH_MI (the newest when two are as near). notesBySeg = {segKey: [note]}. */
export function matchNotes(sites, notesBySeg) {
  const out = {};
  sites.forEach((s) => {
    const list = (notesBySeg[s.segKey] || []).filter((n) => n.analysis === s.analysis && n.direction === s.direction
      && s.mpStart != null && Math.abs(n.mpStart - s.mpStart) <= MATCH_MI + 1e-9);
    if (!list.length) return;
    list.sort((a, b) => Math.abs(a.mpStart - s.mpStart) - Math.abs(b.mpStart - s.mpStart) || b.id - a.id);
    [out[s.id]] = list;
  });
  return out;
}
